import { useMemo } from 'react';
import { mapEntityParams, predefinedTemplates } from 'constants/entities';
import { useAppSelector } from 'hooks';
import { useFavoriteObjects } from 'hooks/useFavoriteObjects';
import { ISelectOption } from 'interfaces';
import { predefinedTemplateSelector } from 'store/slices/mapV2/tabsReducer/layersReducer/mapEntitiesSlice/selectors';
import { MilitaryClass } from 'types';

import { Select } from 'components/ui';
import { SelectProps } from 'components/ui/Select';

type MilitaryHierarchySelectProps<T extends string | number | null, P> = Omit<
  SelectProps<T, P>,
  | 'options'
  | 'withSearch'
  | 'isExpandable'
  | 'withEmpty'
  | 'enableFavorites'
  | 'toggleFavorite'
>;

export const MilitaryHierarchySelect = <T extends string | number | null, P>({
  ...props
}: MilitaryHierarchySelectProps<T, P>) => {
  const mapObjectTemplate = useAppSelector((state) =>
    predefinedTemplateSelector(state, predefinedTemplates.MAP_OBJECT)
  );

  const { favorites, toggleFavorite } = useFavoriteObjects();

  const militaryHierarhyOptions = useMemo(() => {
    const typeParam = mapObjectTemplate.parameters.find(
      (param) => param.title === mapEntityParams.TYPE
    );

    const getOptionFromMilitaryClass = (mc: MilitaryClass): ISelectOption => ({
      value: mc.Name,
      label: mc.Name,
      children: (mc.Children ?? []).map(getOptionFromMilitaryClass),
      isFavorite: !!favorites[mc.Name],
    });

    return (typeParam?.settings?.militaryClass ?? []).map(
      getOptionFromMilitaryClass
    );
  }, [mapObjectTemplate, favorites]);

  return (
    <Select
      {...props}
      options={militaryHierarhyOptions}
      withSearch
      isExpandable
      withEmpty={false}
      enableFavorites
      toggleFavorite={toggleFavorite}
    />
  );
};
